<script setup lang="ts">
import SearchBarField from '@/components/Globals/SearchBarField.vue'
import MultiSelect from '@/components/Forms/MultiSelect.vue'
import DailyTemperaturePeriodFields from '@/components/DailyTemperature/DailyTemperaturePeriodFields.vue'
import SensorResource from '@/api/resources/sensor'
import type { IDailyTemperaturePeriodFilter } from '@/interfaces/entities/daily-temperature'
import type { ISensor } from '@/interfaces/entities/sensor'
import type { IShift } from '@/interfaces/entities/shifts'
import { useSuccessStore } from '@/store/success'
import { useErrorStore } from '@/store/error'
import {
  FIELD_INPUT_CLASS,
  buildReadingsQueryParams,
  createDefaultPeriodFilter,
  formatDisplayDate,
  resetShiftIfUnavailable,
  todayISO,
  validatePdfFilters,
} from '@/utils/daily-temperature/period-filter'
import { useI18n } from '@/composable/useI18n'
import { computed, ref, watch } from 'vue'

const props = defineProps<{
  isOpen: boolean
  defaultPrimaryDate?: string
  /** Cópia dos IDs do painel no momento da abertura — só seed; não compartilhar referência. */
  defaultSensorIds?: number[]
  temperatureSensors: ISensor[]
  shifts: IShift[]
  hasShifts: boolean
}>()

const emit = defineEmits<{
  close: []
}>()

const { t } = useI18n()
const { setSuccess } = useSuccessStore()
const { setErrors } = useErrorStore()

const date = ref(todayISO())
const daysBack = ref(1)
const periodFilter = ref<IDailyTemperaturePeriodFilter>(createDefaultPeriodFilter())
const pdfSensorIds = ref<number[]>([])
const isRequestingPdf = ref(false)

const sensorOptions = computed(() => {
  const selectedIds = new Set(pdfSensorIds.value)
  return props.temperatureSensors.map((sensor) => ({
    id: sensor.id,
    value: sensor.id,
    text: sensor.name,
    selected: selectedIds.has(sensor.id),
  }))
})

function resetForm() {
  date.value = props.defaultPrimaryDate || todayISO()
  daysBack.value = 1
  periodFilter.value = createDefaultPeriodFilter()
  pdfSensorIds.value = [...(props.defaultSensorIds ?? [])]
}

function clearFilters() {
  date.value = todayISO()
  daysBack.value = 1
  periodFilter.value = createDefaultPeriodFilter()
  pdfSensorIds.value = []
}

watch(
  () => props.isOpen,
  (open) => {
    if (open) resetForm()
  },
)

watch(
  () => props.hasShifts,
  (nextHasShifts) => {
    periodFilter.value = resetShiftIfUnavailable(periodFilter.value, nextHasShifts)
  },
)

function handleClose() {
  emit('close')
}

function onSensorsSelected(values: Array<string | number>) {
  pdfSensorIds.value = values
    .map((value) => Number(value))
    .filter((id) => Number.isFinite(id))
}

async function requestPdf() {
  const validationError = validatePdfFilters(date.value, periodFilter.value)
  if (validationError) {
    setErrors([t(`daily_temperature.${validationError}`)])
    return
  }

  const normalizedDaysBack = Math.min(Math.max(Math.round(daysBack.value || 1), 1), 31)
  const periodParams = buildReadingsQueryParams(date.value, periodFilter.value, {
    sensorIds: [...pdfSensorIds.value],
  })

  isRequestingPdf.value = true
  try {
    const response = await SensorResource.requestDailyTemperatureReport({
      ...periodParams,
      days_back: normalizedDaysBack,
    })
    handleClose()
    setSuccess([
      t('daily_temperature.success.pdf_requested', {
        message: response.message,
        email: response.email,
      }),
    ])
  } catch {
    // Erros 422/500 já exibidos pelo interceptor global (axios.config.ts).
  } finally {
    isRequestingPdf.value = false
  }
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      @click.self="handleClose">
      <div
        class="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-none bg-white p-6 shadow-xl [&_input]:rounded-none [&_button]:rounded-none">
        <h3 class="text-lg font-semibold text-gray-900">
          {{ t('daily_temperature.pdf_modal.title') }}
        </h3>
        <p class="mt-1 text-sm text-gray-500">
          {{ t('daily_temperature.legend.pdf_hint') }}
        </p>

        <div class="mt-4 flex flex-col gap-4">
          <SearchBarField :label="t('daily_temperature.pdf_modal.date')">
            <input v-model="date" type="date" :max="todayISO()" :class="FIELD_INPUT_CLASS" />
          </SearchBarField>

          <SearchBarField :label="t('daily_temperature.fields.days_back')">
            <input
              v-model.number="daysBack"
              type="number"
              min="1"
              max="31"
              :class="FIELD_INPUT_CLASS" />
            <p class="mt-1 text-xs text-gray-500">
              {{
                t('daily_temperature.pdf_modal.days_back_hint', {
                  date: formatDisplayDate(date),
                })
              }}
            </p>
          </SearchBarField>

          <DailyTemperaturePeriodFields
            v-model:period-filter="periodFilter"
            :shifts="shifts"
            :has-shifts="hasShifts" />

          <SearchBarField :label="t('daily_temperature.fields.sensors')">
            <MultiSelect
              :options="sensorOptions"
              @update:selected-values="onSensorsSelected" />
          </SearchBarField>
        </div>

        <div class="mt-4 flex items-start gap-2 bg-blue-50 p-3 text-xs text-blue-900">
          <span>{{ t('daily_temperature.pdf_modal.async_notice') }}</span>
        </div>

        <div class="mt-6 flex items-center justify-between gap-3">
          <button
            type="button"
            class="rounded-none bg-transparent px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-700 hover:underline"
            @click="clearFilters">
            {{ t('daily_temperature.buttons.clear_filters') }}
          </button>
          <div class="flex items-center gap-3">
            <button
              type="button"
              class="rounded-none border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              @click="handleClose">
              {{ t('daily_temperature.buttons.cancel') }}
            </button>
            <button
              type="button"
              :disabled="isRequestingPdf"
              class="rounded-none bg-blue px-4 py-2 text-sm font-medium text-white hover:bg-blue-2 disabled:opacity-50"
              @click="requestPdf">
              {{
                isRequestingPdf
                  ? t('daily_temperature.buttons.requesting_pdf')
                  : t('daily_temperature.buttons.request_pdf')
              }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
